'use client'

import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Loader2 } from 'lucide-react'
import { getHealthStatus, formatHealthFactorValue, formatLTV } from '@/domain/common/health-factor'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { TransactionErrorPanel } from '@/ui/molecules/transaction-error-panel/TransactionErrorPanel'
import { cn } from '@/ui/utils/style'
import Image from 'next/image'
import { useMemo } from 'react'
import type { ActionHandlerState } from '@/features/actions/logic/types'

interface ECollateralStatusViewProps {
  collateralSymbol: string
  collateralLogoURI?: string
  collateralAsset: string
  collateralDecimals: number
  currentCollateralFormatted: number
  collateralValueUSD: number
  action: 'enable' | 'disable'
  isSimulating: boolean
  simulationSuccess: boolean
  error: Error | null
  errorMessage: string | null
  canExecute: boolean
  isLoading: boolean
  onConfirm: () => void
  currentHealthFactor: number
  newHealthFactor: number
  currentLTV: number
  newLTV: number
  isSimulatingHealthFactor: boolean
  /** Dialog mode: 'collateral' for standard flow, 'controller' for different-market vaults */
  mode?: 'collateral' | 'controller'
  isTimedOut?: boolean
  onRetry?: () => void
  isRetryDisabled?: boolean
}

export function ECollateralStatusView({
  collateralSymbol,
  collateralLogoURI,
  collateralAsset,
  collateralDecimals,
  currentCollateralFormatted,
  collateralValueUSD,
  action,
  isSimulating,
  simulationSuccess,
  error,
  errorMessage,
  canExecute,
  isLoading,
  onConfirm,
  currentHealthFactor,
  newHealthFactor,
  currentLTV,
  newLTV,
  isSimulatingHealthFactor,
  mode = 'collateral',
  isTimedOut = false,
  onRetry,
  isRetryDisabled,
}: ECollateralStatusViewProps) {
  const collateralImage = collateralLogoURI || (collateralSymbol ? getTokenImage(TokenSymbol(collateralSymbol)) : '')
  const currentHealthStatus = getHealthStatus(currentHealthFactor)
  const newHealthStatus = getHealthStatus(newHealthFactor)

  const isControllerMode = mode === 'controller'
  const dialogTitle = isControllerMode
    ? action === 'enable'
      ? 'Enable Controller'
      : 'Disable Controller'
    : action === 'enable'
      ? 'Enable as Collateral'
      : 'Transfer to Savings'

  // Create token for ActionRow
  const collateralToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(collateralSymbol),
        name: collateralSymbol,
        decimals: collateralDecimals,
        address: CheckedAddress(collateralAsset as `0x${string}`),
        unitPriceUsd: '0',
        logoURI: collateralLogoURI,
      }),
    [collateralSymbol, collateralDecimals, collateralAsset, collateralLogoURI],
  )

  // Determine action status
  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isLoading) return 'loading'
    if (!canExecute) return 'disabled'
    if (isSimulating) return 'loading'
    if (simulationSuccess) return 'ready'
    return 'disabled'
  }, [isLoading, canExecute, isSimulating, simulationSuccess])

  return (
    <MultiPanelDialog>
      <DialogTitle className="font-semibold text-white text-xl">{dialogTitle}</DialogTitle>

      <DialogPanel>
        {/* Summary - Highlighted Card */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              {collateralImage && (
                <Image src={collateralImage} alt={collateralSymbol} width={32} height={32} className="rounded-full" />
              )}
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs">{dialogTitle}</span>
                <span className="font-medium text-sm text-white">{collateralSymbol}</span>
              </div>
            </div>
            <span className="font-semibold text-lg text-primary">
              {currentCollateralFormatted.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}
            </span>
          </div>
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          <TransactionOverviewDetailsItem label="Amount">
            {currentCollateralFormatted.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {collateralSymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Value">
            ${collateralValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </TransactionOverviewDetailsItem>

          {isControllerMode ? (
            /* Controller mode: show simplified info about enable/disable */
            <div className="mt-2 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-amber-200/80 text-sm">
              <p>
                {action === 'enable'
                  ? 'This vault is from a different market than your debt. Enabling the controller will register this deposit in the EVC, but it will not contribute to your borrowing capacity for the current position.'
                  : 'This vault is from a different market than your debt. Disabling the controller will unregister this deposit from the EVC. This will not affect your current position.'}
              </p>
            </div>
          ) : (
            /* Collateral mode: show health factor details */
            <>
              <TransactionOverviewDetailsItem label="Current Health Factor">
                {isSimulatingHealthFactor ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Loading...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <currentHealthStatus.icon className={cn('h-4 w-4', currentHealthStatus.color)} />
                      <span className={cn('font-semibold', currentHealthStatus.color)}>
                        {formatHealthFactorValue(currentHealthFactor)}
                      </span>
                    </div>
                    <span className="text-muted-foreground text-xs">LTV: {formatLTV(currentLTV)}</span>
                  </div>
                )}
              </TransactionOverviewDetailsItem>
              <TransactionOverviewDetailsItem
                label={action === 'enable' ? 'New Health Factor (est.)' : 'New Health Factor'}
              >
                {isSimulatingHealthFactor ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Simulating...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-0.5">
                    <div className="flex items-center gap-2">
                      <newHealthStatus.icon className={cn('h-4 w-4', newHealthStatus.color)} />
                      <span className={cn('font-semibold', newHealthStatus.color)}>
                        {action === 'enable' ? '~' : ''}
                        {formatHealthFactorValue(newHealthFactor)}
                      </span>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      LTV: {action === 'enable' ? '~' : ''}
                      {formatLTV(newLTV)}
                    </span>
                  </div>
                )}
              </TransactionOverviewDetailsItem>
            </>
          )}
        </div>

        {/* Timeout error display */}
        {onRetry && <TimeoutErrorDisplay isTimedOut={isTimedOut} onRetry={onRetry} isRetryDisabled={isRetryDisabled} />}

        {/* Error display */}
        {!isTimedOut && <TransactionErrorPanel error={error} displayMessage={errorMessage ?? undefined} />}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.approve} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={collateralToken} className="h-6" />} actionStatus={executeStatus}>
              {dialogTitle}
            </ActionRow.Title>
            <ActionRow.Description
              successMessage={action === 'enable' ? 'Enabled!' : isControllerMode ? 'Disabled!' : 'Transferred!'}
              actionStatus={executeStatus}
              variant="compact"
            />
            <ActionRow.Action onAction={onConfirm} status={executeStatus}>
              {isLoading
                ? 'Confirming...'
                : isSimulating
                  ? 'Simulating...'
                  : action === 'enable'
                    ? 'Enable'
                    : isControllerMode
                      ? 'Disable'
                      : 'Transfer'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
